import { Columns, Skeleton } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import SignInAppTestDesktop from "@shared/assets/signin-asset.png";
import { SkeletonGoogleSSOButton } from "@shared/components/LoginOptions/GoogleSSOButton/SkeletonGoogleSSOButton";
import { SkeletonOrSection } from "@shared/components/LoginOptions/OrSection/SkeletonOrSection";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useQualityStorySignupExperiment } from "@shared/hooks/useQualityStorySignupExperiment/useQualityStorySignupExperiment";
import { sideCarouselSlides } from "@unauthenticated/shared/components/SideCarouselPanel/sideCarouselSlides";
import { SkeletonSignInForm } from "@unauthenticated/shared/components/SignInForm/SkeletonSignInForm";
import { SignInWrapper } from "@unauthenticated/shared/components/SignInWrapper/SignInWrapper";
import { FullBleedShell } from "@unauthenticated/signupSignin/components/FullBleedShell/FullBleedShell";
import * as fullBleedStyles from "@unauthenticated/signupSignin/components/FullBleedShell/FullBleedShell.styles";
import { SignInLayout } from "@unauthenticated/signupSignin/components/SignInLayout/SignInLayout";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./SignInPage.styles";

const SkeletonSignInPage = () => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isQualityStorySignupEnabled = useQualityStorySignupExperiment({ shouldLog: false });
  const showVerticalCarouselSidePanel =
    isQualityStorySignupEnabled && sideCarouselSlides.length > 0;
  const isMobile = !tablet.greaterThan;

  const renderSignInForm = () => (
    <SignInWrapper>
      <Skeleton variant="typography" typographyType="h1" />
      <Spacer height="one" />
      <SkeletonGoogleSSOButton isOrTextRendered />
      <Spacer height="one" />
      <SkeletonSignInForm />
      <Spacer height="doubleHalf" />
      <Skeleton variant="typography" typographyType="body" isFullWidth />
    </SignInWrapper>
  );

  const renderFullBleedSignInForm = () => (
    <SignInWrapper>
      <Spacer height="one" />
      <Skeleton variant="button" isFullWidth />
      <Spacer height="one" />
      <Skeleton variant="button" isFullWidth />
      <Spacer height="one" />
      <Skeleton variant="button" isFullWidth />
      <Spacer height="one" />
      <SkeletonOrSection />
      <Spacer height="one" />
      <SkeletonSignInForm />
      <Spacer height="doubleHalf" />
      <Skeleton variant="typography" typographyType="body" isFullWidth />
    </SignInWrapper>
  );

  const renderContent = () => {
    if (isMobile) {
      return renderSignInForm();
    }

    if (!showVerticalCarouselSidePanel) {
      return (
        <Columns>
          <Columns.Column columnWidth={6}>
            <div css={styles.centeredContentContainer}>
              <img
                src={SignInAppTestDesktop}
                alt={t(l.signIn.signInPage.appBannerAlt)}
                css={styles.bannerImage}
              />
            </div>
          </Columns.Column>
          <Columns.Column columnWidth={6}>
            <div css={styles.centeredContentContainer}>{renderSignInForm()}</div>
          </Columns.Column>
        </Columns>
      );
    }

    return (
      <FullBleedShell
        panel={<Skeleton customCss={fullBleedStyles.panelPlaceholder} />}
        showCountrySelect
        showPlatformLogo
        customCss={styles.fullBleedViewportHeight}
      >
        {renderFullBleedSignInForm()}
      </FullBleedShell>
    );
  };

  return <SignInLayout isFullBleed={showVerticalCarouselSidePanel}>{renderContent()}</SignInLayout>;
};

export { SkeletonSignInPage };
